import React, { Component, createRef } from 'react';
import { Dialog, DialogTitle, DialogContent, IconButton, LinearProgress, Button, TextField, Grid, Divider, Radio, RadioGroup, FormControlLabel, FormControl } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import strings from '../localization';
import { createCriminalRecordDeclaration, getCriminalRecordDeclarationByApplicationId, updateCriminalRecordDeclaration } from '../services/CriminalRecordDeclarationService';
import { uploadFiles } from '../services/FileUpload';
import { withSnackbar } from "notistack";
import SelectControl from '../components/controls/SelectControl';
import DatePickerControl from '../components/controls/DatePickerControl';
import { getContractCategories } from '../constants/ContractCategory';
import { getYesNoOptions } from '../constants/YesNoOptions';
import SignaturePad from "react-signature-canvas";
import UploadSection from '../constants/UploadSection';
import ApplicationFileStatus from '../constants/ApplicationFileStatus';
import { getError, hasError } from '../functions/Validation';
import Validators from '../constants/ValidatorTypes';

/**
 * Criminal Record Declaration Modal
 * Custom form that matches FormStack design
 */
class CriminalRecordDeclarationModal extends Component {
    constructor(props) {
        super(props);

        this.state = {
            data: {
                firstName: props.application?.forename || '',
                lastName: props.application?.surname || '',
                contractCategory: null,
                convictionOffence: '',
                convictionDate: null,
                sentenceType: '',
                sentenceLength: '',
                sentenceCompletedDate: null,
                restrictedFromWorkingWithUnder18: '',
                additionalComments: '',
                hasAnotherConviction: '',
                signature: null,
                // Second conviction fields
                conviction2Offence: '',
                conviction2Date: null,
                conviction2SentenceType: '',
                conviction2Length: '',
                conviction2CompletedDate: null
            },
            errors: {},
            isLoading: false,
            formSubmitted: false,
            existingDeclarationId: null // Track if we're editing an existing declaration
        };

        this.signaturePadRef = null;
        this.handleSubmit = this.handleSubmit.bind(this);
        this.changeData = this.changeData.bind(this);
        this.clearSignature = this.clearSignature.bind(this);
        this.resizeCanvas = this.resizeCanvas.bind(this);
    }

    componentDidMount() {
        // Auto-populate first name and last name from application
        if (this.props.application) {
            this.setState({
                data: {
                    ...this.state.data,
                    firstName: this.props.application.forename || '',
                    lastName: this.props.application.surname || ''
                }
            });
        }

        // Fetch existing criminal record declaration if modal is open and applicationId is available
        if (this.props.open && this.props.applicationId && this.props.applicationId !== -1) {
            this.fetchExistingDeclaration();
        }

        // Resize signature canvas to match container
        setTimeout(() => {
            if (this.signaturePadRef) {
                this.resizeCanvas();
            }
        }, 100);
    }

    componentDidUpdate(prevProps) {
        // When modal opens (open changes from false to true), fetch existing data
        if (!prevProps.open && this.props.open) {
            console.log('Modal opened - fetching existing declaration');
            if (this.props.applicationId && this.props.applicationId !== -1) {
                this.fetchExistingDeclaration();
            }
        }
        
        // When modal closes, reset form state (optional - for clean slate on next open)
        if (prevProps.open && !this.props.open) {
            console.log('Modal closed - resetting form state');
            // Don't reset here - we want to keep the data for when they reopen
        }
    }

    /**
     * Fetch existing criminal record declaration and pre-populate form
     */
    async fetchExistingDeclaration() {
        // First, reset form to initial state
        const initialData = {
            firstName: this.props.application?.forename || '',
            lastName: this.props.application?.surname || '',
            contractCategory: null,
            convictionOffence: '',
            convictionDate: null,
            sentenceType: '',
            sentenceLength: '',
            sentenceCompletedDate: null,
            restrictedFromWorkingWithUnder18: '',
            additionalComments: '',
            hasAnotherConviction: '',
            signature: null,
            // Second conviction fields
            conviction2Offence: '',
            conviction2Date: null,
            conviction2SentenceType: '',
            conviction2Length: '',
            conviction2CompletedDate: null
        };
        
        this.setState({ 
            existingDeclarationId: null,
            data: initialData
        });
        
        // Clear signature canvas
        if (this.signaturePadRef) {
            this.signaturePadRef.clear();
        }
        
        try {
            const response = await getCriminalRecordDeclarationByApplicationId(this.props.applicationId);
            
            if (response && response.ok && response.data && response.data.id) {
                const declaration = response.data;
                console.log('Found existing criminal record declaration, pre-populating form:', declaration);
                
                // Convert contractCategory string to object for SelectControl
                let contractCategoryObj = null;
                if (declaration.contractCategory) {
                    const categories = getContractCategories();
                    contractCategoryObj = categories.find(cat => cat.name === declaration.contractCategory) || null;
                }
                
                // Pre-populate form fields
                this.setState({
                    existingDeclarationId: declaration.id,
                    data: {
                        firstName: declaration.firstName || this.props.application?.forename || '',
                        lastName: declaration.lastName || this.props.application?.surname || '',
                        contractCategory: contractCategoryObj,
                        convictionOffence: declaration.convictionOffence || '',
                        convictionDate: declaration.convictionDate ? new Date(declaration.convictionDate) : null,
                        sentenceType: declaration.sentenceType || '',
                        sentenceLength: declaration.sentenceLength || '',
                        sentenceCompletedDate: declaration.sentenceCompletedDate ? new Date(declaration.sentenceCompletedDate) : null,
                        restrictedFromWorkingWithUnder18: declaration.restrictedFromWorkingWithUnder18 ? 'Yes' : 'No',
                        additionalComments: declaration.additionalComments || '',
                        hasAnotherConviction: declaration.hasAnotherConviction ? 'Yes' : 'No',
                        signature: declaration.signatureData || null,
                        // Second conviction fields
                        conviction2Offence: declaration.conviction2Offence || '',
                        conviction2Date: declaration.conviction2Date ? new Date(declaration.conviction2Date) : null,
                        conviction2SentenceType: declaration.conviction2SentenceType || '',
                        conviction2Length: declaration.conviction2Length || '',
                        conviction2CompletedDate: declaration.conviction2CompletedDate ? new Date(declaration.conviction2CompletedDate) : null
                    }
                }, () => {
                    // Load signature into canvas if it exists - wait for canvas to be ready
                    if (declaration.signatureData && this.signaturePadRef) {
                        setTimeout(() => {
                            if (this.signaturePadRef) {
                                const img = new Image();
                                img.onload = () => {
                                    const canvas = this.signaturePadRef.getCanvas();
                                    if (canvas) {
                                        const ctx = canvas.getContext('2d');
                                        ctx.drawImage(img, 0, 0);
                                    }
                                };
                                img.src = declaration.signatureData;
                            }
                        }, 200); // Wait for canvas to be resized
                    }
                });
            } else {
                console.log('No existing criminal record declaration found - form will be empty');
            }
        } catch (error) {
            console.log('No existing criminal record declaration found (error):', error);
            // Form is already reset to initial state, so it's ready for new entry
        }
    }

    /**
     * Resize signature canvas to match its container
     */
    resizeCanvas() {
        if (!this.signaturePadRef) return;

        const canvas = this.signaturePadRef.getCanvas();
        if (!canvas) return;

        const ratio = Math.max(window.devicePixelRatio || 1, 1);
        canvas.width = canvas.offsetWidth * ratio;
        canvas.height = canvas.offsetHeight * ratio;
        canvas.getContext('2d').scale(ratio, ratio);
        this.signaturePadRef.clear(); // Clear after resize to avoid stretching
    }

    validationList = {
        firstName: [{ type: Validators.REQUIRED }],
        lastName: [{ type: Validators.REQUIRED }],
        contractCategory: [{ type: Validators.SELECTED_OPTION }],
        convictionOffence: [{ type: Validators.REQUIRED }],
        convictionDate: [{ type: Validators.REQUIRED }],
        sentenceType: [{ type: Validators.REQUIRED }],
        sentenceLength: [{ type: Validators.REQUIRED }],
        sentenceCompletedDate: [{ type: Validators.REQUIRED }],
        restrictedFromWorkingWithUnder18: [{ type: Validators.REQUIRED }],
        hasAnotherConviction: [{ type: Validators.REQUIRED }]
    };

    changeData(event) {
        const { name, value } = event.target;
        this.setState(prevState => ({
            data: {
                ...prevState.data,
                [name]: value
            }
        }));
    }

    clearSignature() {
        if (this.signaturePadRef) {
            this.signaturePadRef.clear();
        }
    }

    validate() {
        let errors = {};

        // Validate required fields
        for (let field in this.validationList) {
            const rules = this.validationList[field];
            const value = this.state.data[field];

            for (let rule of rules) {
                if (rule.type === Validators.REQUIRED && (!value || value === '')) {
                    errors[field] = [{ message: 'This field is required' }];
                    break;
                }
                if (rule.type === Validators.SELECTED_OPTION && (!value || value === null)) {
                    errors[field] = [{ message: 'Please select an option' }];
                    break;
                }
            }
        }

        // Validate signature
        if (this.signaturePadRef && this.signaturePadRef.isEmpty()) {
            errors['signature'] = [{ message: 'Signature is required' }];
        }

        this.setState({ errors });
        return Object.keys(errors).length === 0;
    }

    /**
     * Handle form submission
     */
    async handleSubmit() {
        if (!this.validate()) {
            this.props.enqueueSnackbar('Please fill in all required fields', { variant: 'error' });
            return;
        }

        this.setState({ isLoading: true });

        try {
            // Get signature data
            const signatureData = this.signaturePadRef 
                ? this.signaturePadRef.getTrimmedCanvas().toDataURL('image/png')
                : null;

            // Prepare declaration data
            const declaration = {
                applicationId: this.props.applicationId,
                firstName: this.state.data.firstName,
                lastName: this.state.data.lastName,
                contractCategory: typeof this.state.data.contractCategory === 'string' 
                    ? this.state.data.contractCategory 
                    : (this.state.data.contractCategory?.name || ''),
                convictionOffence: this.state.data.convictionOffence,
                convictionDate: this.state.data.convictionDate,
                sentenceType: this.state.data.sentenceType,
                sentenceLength: this.state.data.sentenceLength,
                sentenceCompletedDate: this.state.data.sentenceCompletedDate,
                restrictedFromWorkingWithUnder18: this.state.data.restrictedFromWorkingWithUnder18 === 'Yes',
                additionalComments: this.state.data.additionalComments || '',
                hasAnotherConviction: this.state.data.hasAnotherConviction === 'Yes',
                // Second conviction details (if applicable)
                conviction2Offence: this.state.data.conviction2Offence || null,
                conviction2Date: this.state.data.conviction2Date || null,
                conviction2SentenceType: this.state.data.conviction2SentenceType || null,
                conviction2Length: this.state.data.conviction2Length || null,
                conviction2CompletedDate: this.state.data.conviction2CompletedDate || null,
                signatureData: signatureData
            };

            // Submit to backend - update if editing, create if new
            let response;
            if (this.state.existingDeclarationId) {
                // Include the ID in the declaration object for update
                // The backend expects declaration.Id to match the id parameter
                declaration.id = this.state.existingDeclarationId;
                console.log('Updating existing declaration ID:', this.state.existingDeclarationId);
                console.log('Submitting declaration with ID:', declaration);
                response = await updateCriminalRecordDeclaration(this.state.existingDeclarationId, declaration);
            } else {
                console.log('Creating new declaration');
                console.log('Submitting declaration:', declaration);
                response = await createCriminalRecordDeclaration(declaration);
            }

            if (response && response.ok) {
                console.log('Declaration saved successfully, now uploading document...');
                
                // Generate and upload document - WAIT for it to complete
                // Pass the signatureData we already captured
                try {
                    await this.generateAndUploadPDF(response.data, signatureData);
                    console.log('Document upload completed');
                } catch (uploadError) {
                    console.error('Error during document upload:', uploadError);
                    // Continue even if upload fails
                }

                this.props.enqueueSnackbar('Criminal record declaration submitted successfully', { variant: 'success' });
                
                this.setState({ 
                    isLoading: false,
                    formSubmitted: true 
                });

                // Close modal after a short delay
                setTimeout(() => {
                    if (this.props.onSubmitSuccess) {
                        this.props.onSubmitSuccess(response.data);
                    }
                    this.props.onClose();
                    // Don't reload the page - just refresh files if callback provided
                    if (this.props.onFilesRefresh) {
                        this.props.onFilesRefresh();
                    }
                }, 1500);
            } else {
                this.props.enqueueSnackbar('Error submitting criminal record declaration', { variant: 'error' });
                this.setState({ isLoading: false });
            }
        } catch (error) {
            console.error('Error saving criminal record declaration:', error);
            this.props.enqueueSnackbar('Error submitting criminal record declaration', { variant: 'error' });
            this.setState({ isLoading: false });
        }
    }

    /**
     * Generate and upload Criminal Record Declaration document as PNG image
     */
    async generateAndUploadPDF(declarationData, signatureDataURL) {
        console.log('Starting document upload process...');
        console.log('Application ID:', this.props.applicationId);
        
        try {
            // Use the signature data URL passed as parameter
            if (!signatureDataURL) {
                console.error('Signature data not available');
                throw new Error('Signature data not available');
            }

            console.log('Generating HTML document...');
            
            // Generate HTML content for the declaration
            const htmlContent = this.generatePDFHTML({...declarationData, signatureData: signatureDataURL});
            
            // Convert HTML to Blob
            const blob = new Blob([htmlContent], { type: 'text/html' });
            console.log('HTML Blob created, size:', blob.size);

            if (!blob || blob.size === 0) {
                console.error('Failed to generate HTML blob or blob is empty');
                throw new Error('Failed to generate HTML blob');
            }

            // Create FormData for file upload
            console.log('Creating FormData...');
            const applicationFile = new FormData();
            applicationFile.append('application', this.props.applicationId);
            applicationFile.append('name', `Criminal_Record_Declaration_${Date.now()}.html`);
            applicationFile.append('content', blob);
            applicationFile.append('fileType', 'text/html');
            applicationFile.append('type', UploadSection.Criminal_Record_Declaration);
            applicationFile.append('applicationFileStatus', ApplicationFileStatus.ON_HOLD);
            applicationFile.append('documentType', 28); // Criminal Record document type

            console.log('FormData created with:', {
                application: this.props.applicationId,
                fileType: 'text/html',
                type: UploadSection.Criminal_Record_Declaration,
                blobSize: blob.size
            });

            // Upload file
            console.log('Calling uploadFiles API...');
            const uploadResponse = await uploadFiles(applicationFile);
            console.log('Upload response:', uploadResponse);

            if (uploadResponse && uploadResponse.ok) {
                console.log('✅ Criminal record declaration document uploaded successfully!');
                console.log('File details:', {
                    id: uploadResponse.data.id,
                    name: uploadResponse.data.name,
                    fileType: uploadResponse.data.fileType,
                    type: uploadResponse.data.type
                });
                return uploadResponse.data;
            } else {
                console.error('❌ Failed to upload criminal record declaration document', uploadResponse);
                throw new Error('Upload failed: ' + JSON.stringify(uploadResponse));
            }

        } catch (error) {
            console.error('❌ Error uploading criminal record declaration document:', error);
            throw error;
        }
    }

    /**
     * Generate HTML content for PDF
     */
    generatePDFHTML(data) {
        return `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Criminal Record Declaration</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 20px; }
                    h1 { color: #333; background-color: #f4d03f; padding: 15px; text-align: center; }
                    h2 { color: #555; margin-top: 30px; }
                    .field { margin: 15px 0; }
                    .label { font-weight: bold; color: #666; }
                    .value { margin-left: 10px; }
                    .signature { border: 1px solid #ccc; padding: 10px; margin-top: 10px; }
                    .signature img { max-width: 300px; }
                </style>
            </head>
            <body>
                <h1>Criminal Record Declaration</h1>
                <h2>Appendix to Application form for employment</h2>
                
                <div class="field">
                    <span class="label">Name:</span>
                    <span class="value">${data.firstName} ${data.lastName}</span>
                </div>
                
                <div class="field">
                    <span class="label">Contract Category:</span>
                    <span class="value">${data.contractCategory}</span>
                </div>
                
                <div class="field">
                    <span class="label">What was the conviction for?</span>
                    <span class="value">${data.convictionOffence}</span>
                </div>
                
                <div class="field">
                    <span class="label">Actual date of conviction:</span>
                    <span class="value">${new Date(data.convictionDate).toLocaleDateString()}</span>
                </div>
                
                <div class="field">
                    <span class="label">What type of sentence did you receive?</span>
                    <span class="value">${data.sentenceType}</span>
                </div>
                
                <div class="field">
                    <span class="label">Length of sentence:</span>
                    <span class="value">${data.sentenceLength}</span>
                </div>
                
                <div class="field">
                    <span class="label">Date sentence was completed:</span>
                    <span class="value">${new Date(data.sentenceCompletedDate).toLocaleDateString()}</span>
                </div>
                
                <div class="field">
                    <span class="label">Restricted from working with anyone under the age of 18?</span>
                    <span class="value">${data.restrictedFromWorkingWithUnder18 ? 'Yes' : 'No'}</span>
                </div>
                
                ${data.additionalComments ? `
                <div class="field">
                    <span class="label">Additional Comments:</span>
                    <div class="value">${data.additionalComments}</div>
                </div>
                ` : ''}
                
                <div class="field">
                    <span class="label">Another conviction to declare?</span>
                    <span class="value">${data.hasAnotherConviction ? 'Yes' : 'No'}</span>
                </div>
                
                ${data.hasAnotherConviction ? `
                <h2 style="background-color: #f4d03f; padding: 10px; text-align: center;">Second Conviction Details</h2>
                
                <div class="field">
                    <span class="label">What was the conviction for?</span>
                    <span class="value">${data.conviction2Offence || 'N/A'}</span>
                </div>
                
                <div class="field">
                    <span class="label">Actual date of conviction:</span>
                    <span class="value">${data.conviction2Date ? new Date(data.conviction2Date).toLocaleDateString() : 'N/A'}</span>
                </div>
                
                <div class="field">
                    <span class="label">What type of sentence did you receive?</span>
                    <span class="value">${data.conviction2SentenceType || 'N/A'}</span>
                </div>
                
                <div class="field">
                    <span class="label">Length of sentence:</span>
                    <span class="value">${data.conviction2Length || 'N/A'}</span>
                </div>
                
                <div class="field">
                    <span class="label">Date sentence was completed:</span>
                    <span class="value">${data.conviction2CompletedDate ? new Date(data.conviction2CompletedDate).toLocaleDateString() : 'N/A'}</span>
                </div>
                ` : ''}
                
                <div class="field">
                    <span class="label">Signature:</span>
                    <div class="signature">
                        ${data.signatureData ? `<img src="${data.signatureData}" />` : 'No signature'}
                    </div>
                </div>
                
                <div class="field">
                    <span class="label">Submission Date:</span>
                    <span class="value">${new Date().toLocaleString()}</span>
                </div>
            </body>
            </html>
        `;
    }

    render() {
        const { open, onClose } = this.props;
        const { isLoading, formSubmitted, data, errors } = this.state;

        return (
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                className="criminal-record-declaration-modal"
                disableBackdropClick={true}
                disableEscapeKeyDown={false}
            >
                <DialogTitle>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: '#b29d24' }}>{strings.criminalRecordDeclaration?.title || 'Criminal Record Declaration'}</span>
                        <IconButton onClick={onClose} disabled={isLoading}>
                            <CloseIcon />
                        </IconButton>
                    </div>
                </DialogTitle>

                <DialogContent style={{ padding: '20px' }}>
                    <style>{`
                        .criminal-record-declaration-modal .datepicker-control-container {
                            position: relative;
                        }
                        .criminal-record-declaration-modal .datepicker-control-container svg {
                            position: absolute;
                            right: 12px;
                            top: 50%;
                            transform: translateY(-50%);
                            pointer-events: none;
                            color: #666;
                        }
                        .criminal-record-declaration-modal .react-datepicker-wrapper {
                            width: 100%;
                        }
                        .criminal-record-declaration-modal .react-datepicker__input-container input {
                            width: 100%;
                            padding: 10px 40px 10px 10px;
                            border: 1px solid #ccc;
                            border-radius: 4px;
                            font-size: 14px;
                        }
                        .criminal-record-declaration-modal .react-datepicker__input-container input:focus {
                            outline: none;
                            border-color: #3f51b5;
                        }
                    `}</style>
                    {isLoading && (
                        <div style={{ padding: '20px', textAlign: 'center' }}>
                            <LinearProgress />
                            <p style={{ marginTop: '10px' }}>Submitting your declaration...</p>
                        </div>
                    )}

                    {formSubmitted && (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <h3 style={{ color: '#4caf50' }}>✓ Declaration Submitted Successfully</h3>
                            <p>Your criminal record declaration has been submitted and a copy will be emailed to you.</p>
                        </div>
                    )}

                    {!isLoading && !formSubmitted && (
                        <div className="criminal-record-form">
                            {/* Header */}
                            <div style={{ backgroundColor: '#f4d03f', padding: '20px', textAlign: 'center', marginBottom: '20px' }}>
                                <h2 style={{ margin: 0 }}>Appendix to Application form for employment</h2>
                            </div>

                            {/* Instructions */}
                            <div style={{ backgroundColor: '#f5f5f5', padding: '15px', marginBottom: '20px' }}>
                                <p>You have indicated on your application form for employment that you have something to declare with reference to the Rehabilitation of Offenders Act 1974.</p>
                                <p><strong>Once completed, this information will be sent directly to the Human Resources team.</strong></p>
                                <p>RAS will take every possible step to ensure that no person seeking employment with us will receive less favourable treatment or will be disadvantaged by any criminal convictions.</p>
                            </div>

                            {/* Form Header */}
                            <div style={{ backgroundColor: '#f4d03f', padding: '15px', textAlign: 'center', marginBottom: '30px' }}>
                                <h3 style={{ margin: 0 }}>Criminal Record Declaration</h3>
                            </div>

                            {/* Disclaimer */}
                            <div style={{ marginBottom: '30px', fontSize: '14px', lineHeight: '1.6' }}>
                                <p>Criminal records will be taken into account for recruitment purposes, only when the conviction is relevant and all information provided will be treated in the strictest confidence. You are not required to disclose convictions which are 'spent' under the Rehabilitation of Offenders Act 1974.</p>
                                <p>Having an 'unspent' conviction will not necessarily bar you from employment. This will depend upon the circumstances and background to your offence(s).</p>
                                <p>We encourage you to be open and honest with your answers. If it is found that you have made a false declaration, then this could lead to disciplinary action which may result in dismissal.</p>
                            </div>

                            <form id="criminal-record-declaration-form">
                                <Grid container spacing={3}>
                                    {/* Full Name */}
                                    <Grid item xs={12}>
                                        <h4>Full name*</h4>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <div className={'select-container' + (hasError(errors, 'firstName') ? ' error' : '')}>
                                            <label>First Name</label>
                                            <TextField
                                                fullWidth
                                                name='firstName'
                                                onChange={this.changeData}
                                                value={data.firstName}
                                                variant='outlined'
                                                error={hasError(errors, 'firstName')}
                                                helperText={getError(errors, 'firstName')}
                                            />
                                        </div>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <div className={'select-container' + (hasError(errors, 'lastName') ? ' error' : '')}>
                                            <label>Last Name</label>
                                            <TextField
                                                fullWidth
                                                name='lastName'
                                                onChange={this.changeData}
                                                value={data.lastName}
                                                variant='outlined'
                                                error={hasError(errors, 'lastName')}
                                                helperText={getError(errors, 'lastName')}
                                            />
                                        </div>
                                    </Grid>

                                    {/* Contract Category */}
                                    <Grid item xs={12}>
                                        <div className={'select-container' + (hasError(errors, 'contractCategory') ? ' error' : '')}>
                                            <label>Which of the following contract categories are you applying to work within?*</label>
                                            <SelectControl
                                                isSearchable={false}
                                                placeholder="Please select"
                                                options={getContractCategories()}
                                                fullWidth
                                                name='contractCategory'
                                                onChange={this.changeData}
                                                value={data.contractCategory}
                                                nameKey={'name'}
                                                valueKey={'value'}
                                                selected={data.contractCategory}
                                            />
                                            {hasError(errors, 'contractCategory') && <p className='select-error'>This field is required</p>}
                                        </div>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Divider style={{ margin: '20px 0', backgroundColor: '#f4d03f', height: '3px' }} />
                                        <p style={{ fontWeight: 'bold', marginTop: '20px' }}>
                                            Please give details of criminal convictions that are unspent under the provisions of the Rehabilitation of Offenders Act 1974. 
                                            Also include (other than as a witness) if you have ever appeared or if you are due to appear before a criminal, civil or military court, 
                                            or have you ever received an official police caution or been placed on police bail or any ongoing police enquiries.
                                        </p>
                                        <Divider style={{ margin: '20px 0', backgroundColor: '#f4d03f', height: '3px' }} />
                                    </Grid>

                                    {/* Conviction Offence */}
                                    <Grid item xs={12}>
                                        <div className={'select-container' + (hasError(errors, 'convictionOffence') ? ' error' : '')}>
                                            <label>What was the conviction for?*</label>
                                            <TextField
                                                fullWidth
                                                name='convictionOffence'
                                                onChange={this.changeData}
                                                value={data.convictionOffence}
                                                variant='outlined'
                                                placeholder="Please give the proper name for the offence"
                                                error={hasError(errors, 'convictionOffence')}
                                                helperText={getError(errors, 'convictionOffence')}
                                            />
                                        </div>
                                    </Grid>

                                    {/* Conviction Date and Sentence Type */}
                                    <Grid item xs={6}>
                                        <div className={'select-container datepicker-wrapper' + (hasError(errors, 'convictionDate') ? ' error' : '')} 
                                             id="convictionDate"
                                             style={{ position: 'relative' }}>
                                            <label>Please give the actual date of conviction*</label>
                                            <div style={{ position: 'relative' }}>
                                                <DatePickerControl
                                                    date={data.convictionDate}
                                                    name={'convictionDate'}
                                                    placeholder={'DD MMMM YYYY'}
                                                    onChange={this.changeData}
                                                    dateFormat='dd/MM/yyyy'
                                                    hasError={hasError(errors, 'convictionDate')}
                                                    error={getError(errors, 'convictionDate')}
                                                    withPortal={true}
                                                />
                                            </div>
                                        </div>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <div className={'select-container' + (hasError(errors, 'sentenceType') ? ' error' : '')}>
                                            <label>What type of sentence did you receive?*</label>
                                            <TextField
                                                fullWidth
                                                name='sentenceType'
                                                onChange={this.changeData}
                                                value={data.sentenceType}
                                                variant='outlined'
                                                placeholder="i.e. Custodial, Probation, Community Service, Fine, etc"
                                                error={hasError(errors, 'sentenceType')}
                                                helperText={getError(errors, 'sentenceType')}
                                            />
                                        </div>
                                    </Grid>

                                    {/* Sentence Length and Completion Date */}
                                    <Grid item xs={6}>
                                        <div className={'select-container' + (hasError(errors, 'sentenceLength') ? ' error' : '')}>
                                            <label>What was the length of the actual sentence that you were given?*</label>
                                            <TextField
                                                fullWidth
                                                name='sentenceLength'
                                                onChange={this.changeData}
                                                value={data.sentenceLength}
                                                variant='outlined'
                                                error={hasError(errors, 'sentenceLength')}
                                                helperText={getError(errors, 'sentenceLength')}
                                            />
                                        </div>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <div className={'select-container datepicker-wrapper' + (hasError(errors, 'sentenceCompletedDate') ? ' error' : '')} 
                                             id="sentenceCompletedDate"
                                             style={{ position: 'relative' }}>
                                            <label>Please give the date in which it was completed*</label>
                                            <div style={{ position: 'relative' }}>
                                                <DatePickerControl
                                                    date={data.sentenceCompletedDate}
                                                    name={'sentenceCompletedDate'}
                                                    placeholder={'DD MMMM YYYY'}
                                                    onChange={this.changeData}
                                                    dateFormat='dd/MM/yyyy'
                                                    hasError={hasError(errors, 'sentenceCompletedDate')}
                                                    error={getError(errors, 'sentenceCompletedDate')}
                                                    withPortal={true}
                                                />
                                            </div>
                                        </div>
                                    </Grid>

                                    {/* Restrictions */}
                                    <Grid item xs={12}>
                                        <div className={'select-container' + (hasError(errors, 'restrictedFromWorkingWithUnder18') ? ' error' : '')}>
                                            <label style={{ marginBottom: '10px', display: 'block', fontWeight: 'bold' }}>
                                                Are you restricted from working with anyone under the age of 18?*
                                            </label>
                                            <FormControl component="fieldset" error={hasError(errors, 'restrictedFromWorkingWithUnder18')}>
                                                <RadioGroup
                                                    name='restrictedFromWorkingWithUnder18'
                                                    value={data.restrictedFromWorkingWithUnder18}
                                                    onChange={this.changeData}
                                                >
                                                    <FormControlLabel 
                                                        value="Yes" 
                                                        control={<Radio color="primary" />} 
                                                        label="Yes" 
                                                    />
                                                    <FormControlLabel 
                                                        value="No" 
                                                        control={<Radio color="primary" />} 
                                                        label="No" 
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                            {hasError(errors, 'restrictedFromWorkingWithUnder18') && <p className='select-error' style={{ color: '#f44336', fontSize: '12px' }}>This field is required</p>}
                                        </div>

                                        {/* Conditional Warning Message */}
                                        {data.restrictedFromWorkingWithUnder18 === 'Yes' && (
                                            <div style={{ 
                                                backgroundColor: '#ffebee', 
                                                border: '1px solid #ef5350',
                                                padding: '20px', 
                                                marginTop: '15px',
                                                borderRadius: '4px',
                                                textAlign: 'center'
                                            }}>
                                                <p style={{ 
                                                    color: '#c62828', 
                                                    fontWeight: 'bold', 
                                                    fontSize: '16px',
                                                    margin: '0 0 10px 0' 
                                                }}>
                                                    Unfortunately we will be unable to continue your application at this stage.
                                                </p>
                                                <p style={{ color: '#666', margin: 0 }}>
                                                    We wish you well in your search for work.
                                                </p>
                                            </div>
                                        )}
                                    </Grid>

                                    {/* Additional Comments */}
                                    <Grid item xs={12}>
                                        <div className={'select-container'}>
                                            <label>You are welcome to add any additional comments here that you feel may be of benefit to your application</label>
                                            <TextField
                                                fullWidth
                                                name='additionalComments'
                                                onChange={this.changeData}
                                                value={data.additionalComments}
                                                variant='outlined'
                                                multiline
                                                rows={4}
                                            />
                                        </div>
                                    </Grid>

                                    <Grid item xs={12}>
                                        <Divider style={{ margin: '20px 0', backgroundColor: '#f4d03f', height: '3px' }} />
                                    </Grid>

                                    {/* Another Conviction */}
                                    <Grid item xs={12}>
                                        <div className={'select-container' + (hasError(errors, 'hasAnotherConviction') ? ' error' : '')}>
                                            <label style={{ marginBottom: '10px', display: 'block', fontWeight: 'bold' }}>
                                                Do you have another conviction to declare?*
                                            </label>
                                            <FormControl component="fieldset" error={hasError(errors, 'hasAnotherConviction')}>
                                                <RadioGroup
                                                    name='hasAnotherConviction'
                                                    value={data.hasAnotherConviction}
                                                    onChange={this.changeData}
                                                >
                                                    <FormControlLabel 
                                                        value="Yes" 
                                                        control={<Radio color="primary" />} 
                                                        label="Yes" 
                                                    />
                                                    <FormControlLabel 
                                                        value="No" 
                                                        control={<Radio color="primary" />} 
                                                        label="No" 
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                            {hasError(errors, 'hasAnotherConviction') && <p className='select-error' style={{ color: '#f44336', fontSize: '12px' }}>This field is required</p>}
                                        </div>
                                    </Grid>

                                    {/* Second Conviction Details - Conditional */}
                                    {data.hasAnotherConviction === 'Yes' && (
                                        <>
                                            <Grid item xs={12}>
                                                <Divider style={{ margin: '20px 0', backgroundColor: '#f4d03f', height: '3px' }} />
                                                <div style={{ 
                                                    backgroundColor: '#f4d03f', 
                                                    padding: '15px', 
                                                    textAlign: 'center', 
                                                    marginBottom: '20px',
                                                    fontSize: '24px',
                                                    fontWeight: 'bold'
                                                }}>
                                                    2
                                                </div>
                                            </Grid>

                                            {/* Second Conviction Offence */}
                                            <Grid item xs={12}>
                                                <div className={'select-container'}>
                                                    <label>What was the conviction for?*</label>
                                                    <TextField
                                                        fullWidth
                                                        name='conviction2Offence'
                                                        onChange={this.changeData}
                                                        value={data.conviction2Offence}
                                                        variant='outlined'
                                                        placeholder="Please give the proper name for the offence"
                                                    />
                                                </div>
                                            </Grid>

                                            {/* Second Conviction Date and Sentence Type */}
                                            <Grid item xs={6}>
                                                <div className={'select-container datepicker-wrapper'} id="conviction2Date">
                                                    <label>Please give the actual date of conviction*</label>
                                                    <div style={{ position: 'relative' }}>
                                                        <DatePickerControl
                                                            date={data.conviction2Date}
                                                            name={'conviction2Date'}
                                                            placeholder={'DD MMMM YYYY'}
                                                            onChange={this.changeData}
                                                            dateFormat='dd/MM/yyyy'
                                                            withPortal={true}
                                                        />
                                                    </div>
                                                </div>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <div className={'select-container'}>
                                                    <label>What type of sentence did you receive?*</label>
                                                    <TextField
                                                        fullWidth
                                                        name='conviction2SentenceType'
                                                        onChange={this.changeData}
                                                        value={data.conviction2SentenceType}
                                                        variant='outlined'
                                                        placeholder="i.e. Custodial, Probation, Community Service, Fine, etc"
                                                    />
                                                </div>
                                            </Grid>

                                            {/* Second Sentence Length and Completion Date */}
                                            <Grid item xs={6}>
                                                <div className={'select-container'}>
                                                    <label>What was the length of the actual sentence that you were given?*</label>
                                                    <TextField
                                                        fullWidth
                                                        name='conviction2Length'
                                                        onChange={this.changeData}
                                                        value={data.conviction2Length}
                                                        variant='outlined'
                                                    />
                                                </div>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <div className={'select-container datepicker-wrapper'} id="conviction2CompletedDate">
                                                    <label>Please give the date in which it was completed*</label>
                                                    <div style={{ position: 'relative' }}>
                                                        <DatePickerControl
                                                            date={data.conviction2CompletedDate}
                                                            name={'conviction2CompletedDate'}
                                                            placeholder={'DD MMMM YYYY'}
                                                            onChange={this.changeData}
                                                            dateFormat='dd/MM/yyyy'
                                                            withPortal={true}
                                                        />
                                                    </div>
                                                </div>
                                            </Grid>
                                        </>
                                    )}

                                    <Grid item xs={12}>
                                        <Divider style={{ margin: '20px 0', backgroundColor: '#f4d03f', height: '3px' }} />
                                    </Grid>

                                    {/* Signature */}
                                    <Grid item xs={12}>
                                        <div className={'select-container' + (hasError(errors, 'signature') ? ' error' : '')}>
                                            <label>Signature*</label>
                                            <div className={'signature-pad'} style={{ 
                                                border: '2px solid #ccc', 
                                                borderRadius: '4px', 
                                                backgroundColor: '#fff',
                                                padding: '10px'
                                            }}>
                                                <SignaturePad
                                                    ref={(ref) => { this.signaturePadRef = ref }}
                                                />
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
                                                <Button onClick={this.clearSignature} style={{ color: '#666', textTransform: 'lowercase' }}>
                                                    clear
                                                </Button>
                                                <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
                                                    Use your mouse or finger to draw your signature above
                                                </p>
                                            </div>
                                            {hasError(errors, 'signature') && <p className='select-error' style={{ color: '#f44336', fontSize: '12px' }}>Signature is required</p>}
                                        </div>
                                    </Grid>

                                    {/* Disclaimer */}
                                    <Grid item xs={12}>
                                        <div style={{ backgroundColor: '#f5f5f5', padding: '15px', marginTop: '20px', fontSize: '13px' }}>
                                            <p style={{ fontWeight: 'bold', margin: '0 0 10px 0' }}>
                                                The information disclosed on this form is for recruitment purposes and will only be kept by Human Resources, if relevant to do so.
                                            </p>
                                        </div>
                                    </Grid>

                                    {/* Submit Button */}
                                    <Grid item xs={12}>
                                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                                            <Button 
                                                variant="contained" 
                                                style={{ 
                                                    backgroundColor: data.restrictedFromWorkingWithUnder18 === 'Yes' ? '#999' : '#b29d24', 
                                                    color: 'white',
                                                    padding: '12px 40px',
                                                    fontSize: '16px'
                                                }}
                                                onClick={this.handleSubmit}
                                                disabled={isLoading || data.restrictedFromWorkingWithUnder18 === 'Yes'}
                                            >
                                                Submit
                                            </Button>
                                            {data.restrictedFromWorkingWithUnder18 === 'Yes' && (
                                                <p style={{ color: '#c62828', marginTop: '10px', fontSize: '13px' }}>
                                                    Submission is disabled because you are restricted from working with individuals under 18.
                                                </p>
                                            )}
                                        </div>
                                    </Grid>
                                </Grid>
                            </form>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        );
    }
}

export default withSnackbar(CriminalRecordDeclarationModal);